<template>
  <div class="video-player-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading video...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <i class="fas fa-exclamation-circle"></i>
      <h2>Unable to Load Video</h2>
      <p>{{ error }}</p>
      <button @click="loadVideo" class="btn btn-primary">Try Again</button>
      <router-link :to="`/learning/courses/${courseId}`" class="btn btn-secondary">
        Back to Course
      </router-link>
    </div>

    <!-- Video Player Content -->
    <div v-else-if="video" class="player-content">
      <!-- Breadcrumb Navigation -->
      <nav class="breadcrumb">
        <router-link to="/learning">Course Catalog</router-link>
        <span class="separator">/</span>
        <router-link :to="`/learning/courses/${courseId}`">{{ course?.title || 'Course' }}</router-link>
        <span class="separator">/</span>
        <span class="current">{{ video.title }}</span>
      </nav>

      <!-- Quick Navigation Bar -->
      <div class="quick-nav-bar">
        <router-link
          v-if="returnToConsultLink"
          :to="returnToConsultLink"
          class="nav-btn back-consult-btn"
        >
          <i class="fas fa-arrow-left"></i>
          Back to Consult Results
        </router-link>
        <router-link
          v-else
          :to="`/learning/courses/${courseId}`"
          class="nav-btn back-btn"
        >
          <i class="fas fa-arrow-left"></i>
          Back to Course
        </router-link>
        
        <div class="nav-center">
          <span class="video-progress-indicator" v-if="course?.user_progress">
            <i class="fas fa-chart-line"></i>
            {{ Math.round(course.user_progress.progress_percentage || 0) }}% Complete
          </span>
        </div>
        
        <router-link 
          v-if="courseHasQuiz && !quizPassed && allVideosCompleted"
          :to="`/learning/courses/${courseId}/quiz`" 
          class="nav-btn quiz-btn"
        >
          <i class="fas fa-clipboard-check"></i>
          Take Quiz
        </router-link>
        <span v-else-if="courseHasQuiz && quizPassed" class="quiz-passed-badge">
          <i class="fas fa-check-circle"></i>
          Quiz Passed
        </span>
      </div>

      <!-- NEW: 3-Column Grid Layout -->
      <div class="player-content-grid">
        
        <!-- Left Column: Course Materials -->
        <CourseMaterialsPanel
          :materials="video.course_materials || []"
          class="materials-panel"
        />

        <!-- Center Column: Video Player -->
        <div class="video-section-center">
          
          <!-- Video Title - Above Everything -->
          <h1 class="video-title-top">{{ vimeoVideoTitle || video.title }}</h1>
          <p v-if="vimeoVideoTitle && vimeoVideoTitle !== video.title" class="video-subtitle">
            Course: {{ video.title }}
          </p>
      
          <!-- Main Video Area (existing code moved here) -->
          <div class="video-section">
            <div class="video-container">
              <!-- Video Player -->
              <div class="video-wrapper">
            <!-- Vimeo Embed -->
            <iframe
              v-if="isVimeoVideo"
              ref="vimeoIframeRef"
              :key="vimeoEmbedUrl"
              :src="vimeoEmbedUrl"
              @load="handleVimeoIframeLoad"
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              class="video-element"
            ></iframe>
            
            <!-- YouTube Embed -->
            <iframe
              v-else-if="isYouTubeVideo"
              :src="getYouTubeEmbedUrl()"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              class="video-element"
            ></iframe>
            
            <!-- Standard HTML5 Video -->
            <video
              v-else
              ref="videoPlayer"
              :src="getVideoUrl(video.video_file || video.video_url)"
              controls
              @timeupdate="handleTimeUpdate"
              @play="handlePlay"
              @pause="handlePause"
              @ended="handleVideoEnded"
              class="video-element"
            >
              Your browser does not support the video tag.
            </video>
          </div>

          <!-- Preview Upsell Overlay (Phase 3) -->
          <div v-if="previewMode && previewPaused && upsellData" class="preview-upsell-overlay">
            <div class="upsell-card">
              <div class="upsell-icon"><i class="fas fa-lock"></i></div>
              <h3>Preview Complete</h3>
              <p>
                You just watched a free {{ previewDurationSeconds }}-second preview of<br>
                <strong>{{ upsellData.course_title }}</strong>
              </p>
              <p class="upsell-sub">Enroll to watch the full course and earn CE credit.</p>
              <div class="upsell-actions">
                <router-link
                  :to="upsellData.checkout_url"
                  class="btn-upsell-enroll"
                >
                  <i class="fas fa-graduation-cap"></i>
                  {{ upsellData.course_is_free ? 'Enroll Free' : `Enroll · $${upsellData.course_price}` }}
                </router-link>
                <button @click="dismissPreviewUpsell" class="btn-upsell-dismiss">
                  Continue Browsing
                </button>
              </div>
            </div>
          </div>

          <!-- Video Info -->
          <div class="video-info">
            <div class="video-meta">
              <span class="video-duration">
                <i class="fas fa-clock"></i>
                {{ formatDuration(video.duration_seconds) }}
              </span>
              <span v-if="video.instructor_name" class="instructor">
                <i class="fas fa-user"></i>
                {{ video.instructor_name }}
              </span>
              <span v-if="progressPercentage > 0" class="progress-badge">
                <i class="fas fa-check-circle"></i>
                {{ Math.round(progressPercentage) }}% Complete
              </span>
            </div>
            <p v-if="video.description" class="video-description">{{ video.description }}</p>
          </div>

          <!-- Quiz Prompt - Shows when all videos completed but quiz not passed -->
          <div v-if="showQuizPrompt && courseHasQuiz && !quizPassed" class="quiz-prompt-section">
            <div class="quiz-prompt-card">
              <div class="quiz-prompt-icon">
                <i class="fas fa-clipboard-check"></i>
              </div>
              <div class="quiz-prompt-content">
                <h3>Ready for the Final Assessment?</h3>
                <p>You've completed all the videos in this course. Take the quiz to earn your certificate!</p>
                <div class="quiz-prompt-stats" v-if="course">
                  <span><i class="fas fa-question-circle"></i> {{ course.quiz_questions?.length || 0 }} questions</span>
                  <span><i class="fas fa-percent"></i> {{ course.quiz_passing_score || 70 }}% to pass</span>
                  <span v-if="course.credit_hours"><i class="fas fa-award"></i> {{ course.credit_hours }} CE Credits</span>
                </div>
              </div>
              <router-link 
                :to="`/learning/courses/${courseId}/quiz`"
                class="btn btn-primary btn-large quiz-prompt-btn"
              >
                <i class="fas fa-play"></i>
                Take Quiz Now
              </router-link>
            </div>
          </div>

          <!-- Manual Research - Move right after video -->
          <div class="manual-research-section">
            <div class="research-header">
              <h3>
                <i class="fas fa-search"></i>
                Manual Research Search
              </h3>
              <p class="research-subtitle">Search scientific literature for specific topics</p>
            </div>
            <ManualResearchBar
              :video-context="video"
              :medical-terms="transcriptAnalysis?.medical_terms?.terms || []"
            />
          </div>

          <!-- Topics in Video - MOVED TO RIGHT PANEL -->

          <!-- External Resources -->
          <div v-if="resources.length > 0" class="resources-section">
            <h3>
              <i class="fas fa-link"></i>
              Additional Resources
            </h3>
            <div class="resources-list">
              <a
                v-for="resource in resources"
                :key="resource.id"
                :href="resource.url"
                target="_blank"
                rel="noopener noreferrer"
                class="resource-item"
              >
                <i class="fas fa-external-link-alt"></i>
                <div class="resource-content">
                  <h4>{{ resource.title }}</h4>
                  <p v-if="resource.description">{{ resource.description }}</p>
                  <span class="resource-type">{{ resource.resource_type }}</span>
                </div>
              </a>
            </div>
          </div>

          <!-- Smart Research Assistant - Always Show Container -->
          <!-- Analyze Button (shown when no analysis exists) -->
          <div v-if="!transcriptAnalysis && video.video_url" class="analyze-prompt-section">
            <div class="smart-research-header-placeholder">
              <h3>
                <i class="fas fa-robot"></i>
                Smart Research Assistant
                <span class="badge badge-success">AI-Powered</span>
              </h3>
              <p class="smart-subtitle">Automatically detect medical terms and find related research from video content</p>
            </div>
            <button @click="analyzeTranscript" class="btn btn-lg btn-primary" :disabled="analyzingTranscript">
              <i class="fas fa-magic"></i>
              {{ analyzingTranscript ? 'Analyzing Video...' : 'Enable Smart Research Assistant' }}
            </button>
            <p class="analyze-info">
              <i class="fas fa-info-circle"></i>
              Analyzes video transcript to extract medical terms and find related research automatically
            </p>
          </div>

          <!-- Smart Research Sidebar - AI-Powered - Collapsible (shown when analysis exists) -->
          <div v-if="transcriptAnalysis && transcriptAnalysis.medical_terms" class="smart-research-section collapsible-section">
            <div class="section-header" @click="showSmartResearch = !showSmartResearch">
              <div class="smart-research-header-content">
                <h3>
                  <i class="fas fa-robot"></i>
                  Smart Research Assistant
                  <span class="badge badge-success">AI-Powered</span>
                </h3>
                <p class="smart-subtitle">Automatically detected medical terms and related research from video content</p>
              </div>
              <i class="fas" :class="showSmartResearch ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
            <div v-show="showSmartResearch">

            <!-- Medical Terms Timeline -->
            <div v-if="transcriptAnalysis.medical_terms.terms.length > 0" class="medical-terms-timeline">
              <div class="keywords-header">
                <h4><i class="fas fa-stethoscope"></i> Key Topics in This Video</h4>
                <div v-if="transcriptAnalysis.extraction_methods" class="extraction-badge">
                  <i class="fas fa-magic"></i>
                  AI-Enhanced Keywords
                  <span class="badge-tooltip">
                    Combined {{ transcriptAnalysis.extraction_methods.merged_keywords }} keywords from 
                    {{ transcriptAnalysis.extraction_methods.llm_keywords }} LLM + 
                    {{ transcriptAnalysis.extraction_methods.dictionary_terms }} dictionary + 
                    {{ transcriptAnalysis.extraction_methods.chapter_keywords }} chapter analysis
                  </span>
                </div>
              </div>
              <p class="terms-help-text">
                <i class="fas fa-info-circle"></i>
                AI-extracted specific medical terms. Click to search research articles or view context.
              </p>
              <div class="terms-grid">
                <div 
                  v-for="term in transcriptAnalysis.medical_terms.terms.slice(0, 15)" 
                  :key="term.term"
                  class="term-card"
                  :class="[`term-${term.category}`, `importance-${term.importance}`, `source-${term.source}`]"
                >
                  <div class="term-header">
                    <span class="term-name">{{ term.term }}</span>
                    <div class="term-badges">
                      <span v-if="term.importance === 'high'" class="importance-badge high" title="High importance">
                        <i class="fas fa-star"></i>
                      </span>
                      <span v-if="term.source === 'llm'" class="source-badge llm" title="AI-extracted specific term">
                        <i class="fas fa-brain"></i>
                      </span>
                      <span v-if="term.source === 'chapter'" class="source-badge chapter" title="From chapter titles">
                        <i class="fas fa-bookmark"></i>
                      </span>
                      <span v-if="term.mentions > 1" class="mention-badge" :title="`Mentioned ${term.mentions} times`">
                        {{ term.mentions }}x
                      </span>
                    </div>
                  </div>
                  
                  <!-- Context from LLM -->
                  <div v-if="term.context" class="term-context">
                    <i class="fas fa-quote-left"></i>
                    {{ term.context }}
                  </div>
                  
                  <!-- Quality score indicator -->
                  <div v-if="term.score" class="term-score">
                    <div class="score-bar">
                      <div class="score-fill" :style="{ width: (term.score * 100) + '%' }"></div>
                    </div>
                    <span class="score-label">Relevance: {{ Math.round(term.score * 100) }}%</span>
                  </div>
                  
                  <div class="term-actions">
                    <button 
                      @click="quickSearch(term.term)"
                      class="term-action-btn search-btn"
                      title="Search PubMed for research articles"
                    >
                      <i class="fas fa-search"></i>
                      Search Articles
                    </button>
                  </div>
                  
                  <!-- Show all timestamp occurrences with context -->
                  <div v-if="term.timestamps && term.timestamps.length > 0" class="term-timestamps">
                    <div class="timestamps-label">
                      <i class="fas fa-clock"></i>
                      Mentioned {{ term.timestamps.length }} time{{ term.timestamps.length > 1 ? 's' : '' }} in video:
                    </div>
                    <div class="timestamp-list">
                      <div
                        v-for="(timestamp, idx) in term.timestamps.slice(0, 5)"
                        :key="idx"
                        @click="seekToTimestamp(timestamp.start)"
                        class="timestamp-item"
                        :title="`Jump to ${formatTime(timestamp.start)}`"
                      >
                        <div class="timestamp-time">
                          <i class="fas fa-play-circle"></i>
                          {{ formatTime(timestamp.start) }}
                        </div>
                        <div class="timestamp-context" v-if="timestamp.text || timestamp.context">
                          "{{ timestamp.text || timestamp.context }}"
                        </div>
                      </div>
                      <div v-if="term.timestamps.length > 5" class="more-timestamps">
                        <i class="fas fa-ellipsis-h"></i>
                        {{ term.timestamps.length - 5 }} more mention{{ term.timestamps.length - 5 > 1 ? 's' : '' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Manual Search Results (from "Search Articles" button) -->
            <div v-if="researchResults.length > 0" class="manual-search-results">
              <h4>
                <i class="fas fa-search"></i>
                Search Results for "{{ researchQuery }}"
              </h4>
              <p class="search-results-info">Found {{ researchResults.length }} research articles</p>
              
              <div class="articles-list">
                <div 
                  v-for="(article, idx) in researchResults" 
                  :key="idx"
                  class="research-result-item"
                >
                  <h5>{{ article.title }}</h5>
                  <p class="result-abstract">{{ article.abstract }}</p>
                  <div class="result-meta">
                    <span v-if="article.authors"><i class="fas fa-user"></i> {{ article.authors }}</span>
                    <span v-if="article.journal"><i class="fas fa-book"></i> {{ article.journal }}</span>
                    <span v-if="article.year"><i class="fas fa-calendar"></i> {{ article.year }}</span>
                  </div>
                  <a v-if="article.url" :href="article.url" target="_blank" class="view-article-btn">
                    <i class="fas fa-external-link-alt"></i>
                    View on PubMed
                  </a>
                </div>
              </div>
            </div>

            <!-- Auto-Generated Research -->
            <div v-if="smartResearchResults.length > 0" class="auto-research-results">
              <h4><i class="fas fa-flask"></i> Related Scientific Literature</h4>
              <p class="auto-research-info">Based on topics mentioned in this video</p>
              
              <div v-for="(resultSet, index) in smartResearchResults" :key="index" class="research-query-group">
                <div class="query-header">
                  <i class="fas fa-search-plus"></i>
                  <strong>{{ resultSet.query }}</strong>
                </div>
                
                <div class="articles-list">
                  <!-- Similar article display as manual search -->
                  <div 
                    v-for="(article, idx) in getArticlesFromResult(resultSet.articles)" 
                    :key="idx"
                    class="research-result-item"
                  >
                    <h5>{{ article.title }}</h5>
                    <p class="result-abstract">{{ article.abstract }}</p>
                    <div class="result-meta">
                      <span v-if="article.authors"><i class="fas fa-user"></i> {{ article.authors }}</span>
                      <span v-if="article.journal"><i class="fas fa-book"></i> {{ article.journal }}</span>
                      <span v-if="article.year"><i class="fas fa-calendar"></i> {{ article.year }}</span>
                    </div>
                    <a v-if="article.url" :href="article.url" target="_blank" class="view-article-btn">
                      <i class="fas fa-external-link-alt"></i>
                      View on PubMed
                    </a>
                  </div>
                </div>
              </div>
            </div>

            </div>
            <!-- End Smart Research collapsible content -->
          </div>
          <!-- End Smart Research section -->

          <!-- OLD Manual Research Section - REMOVE (we moved it above) -->
          <div class="research-section" style="display: none;">
            <div class="research-header">
              <h3>
                <i class="fas fa-microscope"></i>
                Manual Research Search
              </h3>
              <p class="research-subtitle">Search scientific literature for specific topics</p>
            </div>

            <!-- Search Interface -->
            <div class="research-search">
              <div class="search-input-group">
                <input
                  v-model="researchQuery"
                  type="text"
                  placeholder="Search PubMed, NCBI, and other databases..."
                  class="search-input"
                  @keyup.enter="performResearch"
                />
                <button 
                  @click="performResearch" 
                  class="btn btn-primary search-btn"
                  :disabled="loadingResearch || !researchQuery.trim()"
                >
                  <i class="fas fa-search"></i>
                  Search
                </button>
              </div>
              
              <div class="search-filters">
                <label
                  v-for="connection in availableConnections"
                  :key="connection.id"
                  class="filter-checkbox"
                >
                  <input
                    v-model="selectedConnections"
                    type="checkbox"
                    :value="connection.id"
                  />
                  <i class="fas fa-database"></i>
                  {{ connection.name }}
                </label>
                <div v-if="availableConnections.length === 0" class="no-connections">
                  <small>No API connections available. Set up connections in API Integrations.</small>
                </div>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="loadingResearch" class="research-loading">
              <div class="spinner-small"></div>
              <p>Searching scientific databases...</p>
            </div>

            <!-- Research Results -->
            <div v-else-if="researchResults.length > 0" class="research-results">
              <div class="results-header">
                <h4>Found {{ researchResults.length }} results</h4>
              </div>
              <div class="results-list">
                <div
                  v-for="(result, index) in researchResults"
                  :key="index"
                  class="result-item"
                >
                  <div class="result-header">
                    <h5>{{ result.title }}</h5>
                    <span class="result-source">{{ result.source }}</span>
                  </div>
                  <p v-if="result.abstract" class="result-abstract">{{ result.abstract }}</p>
                  <div class="result-meta">
                    <span v-if="result.authors" class="result-authors">
                      <i class="fas fa-user-friends"></i>
                      {{ result.authors }}
                    </span>
                    <span v-if="result.year" class="result-year">
                      <i class="fas fa-calendar"></i>
                      {{ result.year }}
                    </span>
                    <span v-if="result.journal" class="result-journal">
                      <i class="fas fa-book"></i>
                      {{ result.journal }}
                    </span>
                  </div>
                  <div class="result-actions">
                    <a
                      v-if="result.url"
                      :href="result.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="btn btn-small btn-secondary"
                    >
                      <i class="fas fa-external-link-alt"></i>
                      View Article
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="researchSearched && !loadingResearch" class="research-empty">
              <i class="fas fa-search"></i>
              <p>No results found. Try different keywords or check your database selections.</p>
            </div>

            <!-- Initial State -->
            <div v-else class="research-initial">
              <i class="fas fa-lightbulb"></i>
              <p>Search for peer-reviewed articles, research papers, and scientific literature related to this course topic.</p>
              <div class="quick-searches">
                <button 
                  v-if="video && video.title"
                  @click="quickSearch(video.title)"
                  class="quick-search-btn"
                >
                  Search: "{{ video.title }}"
                </button>
                <button 
                  v-if="course && course.title"
                  @click="quickSearch(course.title)"
                  class="quick-search-btn"
                >
                  Search: "{{ course.title }}"
                </button>
              </div>
            </div>
          </div>
          <!-- End .research-section -->
        </div>
        <!-- End .video-container -->
        </div>
        <!-- End .video-section -->
        </div>
        <!-- End .video-section-center (Center Column) -->

        <!-- Right Column: Topics in this Video -->
        <div class="topics-panel">
          <div class="panel-header">
            <h3>
              <i class="fas fa-list"></i>
              Topics in this Video
            </h3>
            <p class="panel-subtitle">{{ timestamps.length }} key moments</p>
          </div>
          
          <div v-if="timestamps.length > 0" class="topics-list">
            <div
              v-for="timestamp in timestamps"
              :key="timestamp.id"
              class="topic-item"
              :class="{ active: isTimestampActive(timestamp) }"
              @click="seekToTimestamp(timestamp.timestamp_seconds)"
            >
              <div class="topic-time">
                <i class="fas fa-clock"></i>
                {{ formatTime(timestamp.timestamp_seconds) }}
              </div>
              <div class="topic-content">
                <h4>{{ timestamp.title }}</h4>
                <p v-if="timestamp.description">{{ timestamp.description }}</p>
              </div>
              <i class="fas fa-play-circle play-icon"></i>
            </div>
          </div>
          
          <div v-else class="topics-empty">
            <i class="fas fa-list"></i>
            <p>No topics marked yet</p>
          </div>

          <!-- Saved Research for this Course -->
          <div class="saved-research-panel">
            <div class="panel-header saved-research-header">
              <h3>
                <i class="fas fa-bookmark"></i>
                My Research Library
              </h3>
              <p class="panel-subtitle">{{ savedCourseResources.length }} saved articles</p>
            </div>
            
            <div v-if="loadingSavedResearch" class="loading-mini">
              <div class="spinner-small"></div>
            </div>
            
            <div v-else-if="savedCourseResources.length > 0" class="saved-resources-list">
              <div
                v-for="resource in savedCourseResources.slice(0, 5)"
                :key="resource.id"
                class="saved-resource-item"
              >
                <div class="saved-resource-content">
                  <a :href="resource.url" target="_blank" rel="noopener" class="saved-resource-title">
                    {{ truncateTitle(resource.title, 60) }}
                  </a>
                  <div class="saved-resource-meta">
                    <span v-if="resource.journal" class="resource-journal">{{ resource.journal }}</span>
                    <span v-if="resource.publication_date" class="resource-date">{{ resource.publication_date }}</span>
                  </div>
                </div>
                <button 
                  @click="toggleResourceFavorite(resource)"
                  class="btn-icon-mini"
                  :class="{ 'is-favorite': resource.is_favorite }"
                >
                  <i :class="resource.is_favorite ? 'fas fa-star' : 'far fa-star'"></i>
                </button>
              </div>
              
              <router-link 
                v-if="savedCourseResources.length > 5"
                :to="`/learning/courses/${courseId}?tab=research`"
                class="view-all-link"
              >
                View all {{ savedCourseResources.length }} articles
                <i class="fas fa-arrow-right"></i>
              </router-link>
            </div>
            
            <div v-else class="saved-research-empty">
              <i class="fas fa-bookmark"></i>
              <p>Save research articles while searching to build your library for this course</p>
            </div>
          </div>
        </div>

      </div>
      <!-- End 3-Column Grid: player-content-grid -->
      
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { lmsAPI } from '@/services/lmsAPI.js'
import { useToast } from 'vue-toastification'
import Player from '@vimeo/player'
import CourseMaterialsPanel from '@/components/lms/CourseMaterialsPanel.vue'
import ManualResearchBar from '@/components/lms/ManualResearchBar.vue'

export default {
  name: 'CourseVideoPlayer',
  components: {
    CourseMaterialsPanel,
    ManualResearchBar
  },
  props: {
    courseId: {
      type: [String, Number],
      required: true
    },
    videoId: {
      type: [String, Number],
      required: true
    }
  },
  setup(props) {
    const router = useRouter()
    const route = useRoute()
    const toast = useToast()

    const video = ref(null)
    const course = ref(null)
    const timestamps = ref([])
    const resources = ref([])
    const courseVideos = ref([])
    const loading = ref(true)
    const loadingCourseVideos = ref(false)
    const error = ref(null)
    const videoPlayer = ref(null)
    const vimeoPlayer = ref(null)
    const currentTime = ref(0)
    const progressPercentage = ref(0)
    const progressUpdateInterval = ref(null)
    
    // Enrollment and progress state
    const enrollment = ref(null)
    const videoProgress = ref(null)
    const lastPositionSeconds = ref(0)  // Position loaded from server (for restore)
    const maxPositionSeconds = ref(0)   // Highest position reached in this session (never goes down)
    const progressLoaded = ref(false)  // Track when progress API call completes
    const allVideosCompleted = ref(false)
    const showQuizPrompt = ref(false)
    const courseHasQuiz = ref(false)
    const quizPassed = ref(false)

    // Research section state
    const researchQuery = ref('')
    const researchResults = ref([])
    const loadingResearch = ref(false)
    const researchSearched = ref(false)
    const availableConnections = ref([])
    const selectedConnections = ref([])
    
    // Smart Research state
    const transcriptAnalysis = ref(null)
    const smartResearchResults = ref([])
    const analyzingTranscript = ref(false)

    // Preview / paywall state (Phase 3)
    const previewMode = ref(false)
    const previewStart = ref(0)
    const previewEnd = ref(0)     // seconds at which the preview hard-stops
    const previewPaused = ref(false)
    const upsellData = ref(null)  // { course_id, course_title, course_price, course_is_free, checkout_url }
    
    // UI state for collapsible sections
    const showTopics = ref(true)
    const showSmartResearch = ref(false)
    
    // Saved research for this course
    const savedCourseResources = ref([])
    const loadingSavedResearch = ref(false)

    const nextVideo = computed(() => {
      const currentIndex = courseVideos.value.findIndex(v => v.id == props.videoId)
      if (currentIndex >= 0 && currentIndex < courseVideos.value.length - 1) {
        return courseVideos.value[currentIndex + 1]
      }
      return null
    })

    const returnToConsultLink = computed(() => {
      if (route.query.from !== 'consult') return null
      const sessionId = route.query.session
      if (sessionId) {
        return { path: '/learning', query: { session: String(sessionId), tab: 'agent' } }
      }
      return { path: '/learning', query: { tab: 'agent' } }
    })

    const previewDurationSeconds = computed(() => {
      if (previewEnd.value > previewStart.value) {
        return Math.round(previewEnd.value - previewStart.value)
      }
      return 60
    })

    const enforcePreviewBounds = async (player, seconds) => {
      if (!previewMode.value || previewEnd.value <= 0) return
      if (seconds >= previewEnd.value) {
        await player.setCurrentTime(Math.max(previewStart.value, previewEnd.value - 1))
        await player.pause()
        previewPaused.value = true
        return
      }
      if (previewStart.value > 0 && seconds < previewStart.value) {
        await player.setCurrentTime(previewStart.value)
      }
    }

    const dismissPreviewUpsell = () => {
      if (returnToConsultLink.value) {
        router.push(returnToConsultLink.value)
      } else if (course.value?.id) {
        router.push(`/learning/courses/${course.value.id}`)
      } else {
        router.push('/learning')
      }
    }

    const applyPreviewAccess = async (seekSeconds, endSeconds = null) => {
      try {
        const previewResp = await lmsAPI.getVideoPreviewAccess(
          props.videoId,
          seekSeconds,
          endSeconds,
        )
        const pData = previewResp.data
        if (pData.is_enrolled) {
          return { enrolled: true, startAt: seekSeconds }
        }
        previewMode.value = true
        previewStart.value = pData.preview_start ?? 0
        previewEnd.value = pData.preview_end ?? 0
        upsellData.value = pData.upsell
        if (pData.video_url && video.value && !video.value.video_url) {
          video.value = { ...video.value, video_url: pData.video_url }
        }
        return {
          enrolled: false,
          startAt: pData.preview_start ?? seekSeconds,
        }
      } catch (err) {
        console.error('Preview access failed:', err)
        toast.error('Preview unavailable. Enroll to watch the full video.')
        error.value = 'Preview unavailable for this video.'
        return null
      }
    }

    const isVimeoVideo = computed(() => {
      if (!video.value || !video.value.video_url) return false
      return video.value.video_url.includes('vimeo.com')
    })

    const isYouTubeVideo = computed(() => {
      if (!video.value || !video.value.video_url) return false
      return video.value.video_url.includes('youtube.com') || video.value.video_url.includes('youtu.be')
    })

    // Computed max progress percentage based on maxPositionSeconds
    // This represents the HIGHEST progress ever achieved, not current playback position
    const maxProgressPercentage = computed(() => {
      if (!video.value || !video.value.duration_seconds || video.value.duration_seconds <= 0) return 0
      return Math.min(100, (maxPositionSeconds.value / video.value.duration_seconds) * 100)
    })

    // Track if we've already restored progress for this video to prevent duplicate restores
    const progressRestored = ref(false)
    
    // Reactive timestamp used to force iframe reloads for seeking
    const vimeoSeekTimestamp = ref(0)
    
    // Computed property for Vimeo embed URL - caches the value to prevent re-renders
    // Now includes timestamp for progress restoration via vimeoSeekTimestamp
    const vimeoEmbedUrl = computed(() => {
      if (!video.value || !video.value.video_url) return ''
      
      const url = video.value.video_url
      let videoId = ''
      let hashParam = ''
      
      try {
        // Parse the URL to extract video ID and hash parameter
        const urlObj = new URL(url)
        
        // Extract video ID from path
        const pathMatch = urlObj.pathname.match(/\/(\d+)/)
        if (pathMatch) {
          videoId = pathMatch[1]
        }
        
        // Preserve hash parameter if present (required for privacy settings)
        // Format: /video/123456789?h=abc123def
        const hashValue = urlObj.searchParams.get('h')
        if (hashValue) {
          hashParam = hashValue
        }
        
      } catch (e) {
        // Fallback to regex matching if URL parsing fails
        const standardMatch = url.match(/vimeo\.com\/(\d+)/)
        if (standardMatch) {
          videoId = standardMatch[1]
        }
        
        const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)/)
        if (playerMatch) {
          videoId = playerMatch[1]
        }
        
        // Try to extract hash from URL string
        const hashMatch = url.match(/[?&]h=([a-zA-Z0-9]+)/)
        if (hashMatch) {
          hashParam = hashMatch[1]
        }
      }
      
      if (videoId) {
        // Use clean Vimeo embed URL with app_id parameter
        // app_id is required for videos with domain restrictions
        let embedUrl = `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479`
        
        // Add hash if present (required for unlisted/private videos)
        if (hashParam) {
          embedUrl += `&h=${hashParam}`
        }
        
        // Add seek timestamp if present - this is the key to positioning the video on reload
        if (vimeoSeekTimestamp.value > 0) {
          embedUrl += `&autoplay=1&t_seek=${Date.now()}#t=${vimeoSeekTimestamp.value}s`
        }
        
        console.log('[Vimeo Embed] Original URL:', url)
        console.log('[Vimeo Embed] Generated embed URL:', embedUrl)
        
        return embedUrl
      }
      
      return url
    })
    
    // Keep the function for backward compatibility if needed elsewhere
    const getVimeoEmbedUrl = () => vimeoEmbedUrl.value

    const getYouTubeEmbedUrl = () => {
      if (!video.value || !video.value.video_url) return ''
      
      let videoId = ''
      const url = video.value.video_url
      
      // Format: https://www.youtube.com/watch?v=VIDEO_ID
      const watchMatch = url.match(/[?&]v=([^&]+)/)
      if (watchMatch) {
        videoId = watchMatch[1]
      }
      
      // Format: https://youtu.be/VIDEO_ID
      const shortMatch = url.match(/youtu\.be\/([^?]+)/)
      if (shortMatch) {
        videoId = shortMatch[1]
      }
      
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url
    }

    const vimeoPlayerReady = ref(false)
    const vimeoIframeRef = ref(null)
    const vimeoVideoTitle = ref(null)

    const handleVimeoIframeLoad = () => {
      if (!isVimeoVideo.value) return
      
      console.log('🔄 Vimeo Iframe @load event fired - URL:', vimeoEmbedUrl.value)
      
      // If we have an old player, clean it up
      if (vimeoPlayer.value) {
        try {
          console.log('🧹 Cleaning up old Vimeo player instance')
          vimeoPlayer.value.destroy()
        } catch (e) {
          console.warn('Error destroying player:', e)
        }
        vimeoPlayer.value = null
        vimeoPlayerReady.value = false
      }
      
      // Re-initialize with the fresh iframe
      // Use a slightly longer delay to ensure the browser has finished processing the new iframe
      setTimeout(() => {
        console.log('🚀 Calling initializeVimeoPlayer from handleVimeoIframeLoad')
        initializeVimeoPlayer()
      }, 1500)
    }

    const initializeVimeoPlayer = async () => {
      console.log('🎬 initializeVimeoPlayer called. State:', {
        isVimeo: isVimeoVideo.value,
        hasPlayer: !!vimeoPlayer.value,
        iframeRef: !!vimeoIframeRef.value
      })

      // Initialize Vimeo Player once the iframe is loaded
      if (isVimeoVideo.value && !vimeoPlayer.value) {
        // Use the template ref if available, fallback to querySelector
        const iframe = vimeoIframeRef.value || document.querySelector('.video-element')
        
        if (iframe) {
          try {
            console.log('⏳ Creating new Vimeo Player instance')
            const player = new Player(iframe)
            vimeoPlayer.value = player
            
            // Wait for the player to be loaded before allowing seeks
            player.on('loaded', async () => {
              vimeoPlayerReady.value = true
              console.log('✅ Vimeo Player initialized and ready')
              
              // If this was a seek-reload, make sure it's playing
              if (vimeoSeekTimestamp.value > 0) {
                console.log('▶️ Auto-playing after seek-reload')
                try {
                  await player.play()
                } catch (err) {
                  console.warn('Autoplay failed (likely browser policy):', err)
                }
              }

              // Fetch Vimeo video title
              try {
                const title = await player.getVideoTitle()
                vimeoVideoTitle.value = title
                console.log('📹 Vimeo video title:', title)
              } catch (err) {
                console.log('Could not get video title:', err)
              }
              
              // ===== RESTORE PROGRESS (Initial load only) =====
              // If we haven't restored yet, and we have a saved position, and NO reactive seek is active
              // Skip saved progress restore during paywalled preview clips
              if (previewMode.value) {
                return
              }
              if (progressLoaded.value && lastPositionSeconds.value > 0 && !progressRestored.value && vimeoSeekTimestamp.value === 0) {
                progressRestored.value = true
                console.log('📍 Initial progress restore to', lastPositionSeconds.value, 'seconds')
                
                setTimeout(() => {
                  seekToTimestamp(lastPositionSeconds.value)
                }, 500)
              }
            })
            
            // ===== PROGRESS TRACKING EVENT LISTENERS =====
            
            // Track play events - start progress tracking
            player.on('play', () => {
              console.log('▶️ Vimeo video playing - starting progress tracking')
              startProgressTracking()
            })
            
            // Track pause events - save progress immediately
            player.on('pause', () => {
              console.log('⏸️ Vimeo video paused - saving progress')
              saveProgress()
            })
            
            // Track time updates for current position
            player.on('timeupdate', (data) => {
              // data contains: seconds, percent, duration
              currentTime.value = data.seconds
              if (data.duration && video.value) {
                progressPercentage.value = (data.seconds / data.duration) * 100
              }
              if (previewMode.value && previewEnd.value > 0 && data.seconds >= previewEnd.value) {
                enforcePreviewBounds(player, data.seconds)
              }
            })

            player.on('seeked', async (data) => {
              if (previewMode.value && previewEnd.value > 0) {
                await enforcePreviewBounds(player, data.seconds)
              }
            })
            
            // Track video ended - mark as completed
            player.on('ended', () => {
              console.log('🏁 Vimeo video ended - marking as completed')
              saveProgress(true)
              
              // Auto-play next video after a delay
              if (nextVideo.value) {
                setTimeout(() => {
                  toast.info(`Starting next video: ${nextVideo.value.title}`)
                  playNextVideo()
                }, 2000)
              } else {
                // This is the last video - check if quiz is needed
                allVideosCompleted.value = true
                
                if (courseHasQuiz.value && !quizPassed.value) {
                  showQuizPrompt.value = true
                  toast.success('🎉 All videos completed! Click here to take the quiz.', {
                    timeout: 8000,
                    onClick: () => router.push(`/learning/courses/${props.courseId}/quiz`)
                  })
                } else if (!courseHasQuiz.value) {
                  toast.success('🎉 Course completed! Great job!', {
                    timeout: 5000,
                    onClick: () => router.push(`/learning/courses/${props.courseId}`)
                  })
                } else {
                  toast.success('🎉 Course completed! Click here to download your certificate.', {
                    timeout: 5000,
                    onClick: () => router.push(`/learning/courses/${props.courseId}`)
                  })
                }
              }
            })
            
            // Also try to get duration to verify player is working
            try {
              const duration = await player.getDuration()
              console.log('📹 Video duration:', duration, 'seconds')
            } catch (err) {
              console.log('Could not get duration:', err)
            }
          } catch (err) {
            console.error('Error initializing Vimeo player:', err)
          }
        }
      }
    }

    const loadVideo = async () => {
      loading.value = true
      error.value = null

      try {
        const response = await lmsAPI.getVideo(props.videoId)
        video.value = response.data

        // Load timestamps if available
        if (video.value.timestamps) {
          timestamps.value = video.value.timestamps
        }

        // Load resources if available
        if (video.value.resources) {
          resources.value = video.value.resources
        }

        // Initialize Vimeo player after video data is loaded
        // We now rely on @load in the template to call handleVimeoIframeLoad
        // which calls initializeVimeoPlayer. No need for manual timeout here.
      } catch (err) {
        console.error('Error loading video:', err)
        error.value = err.response?.data?.detail || 'Failed to load video'
        toast.error(error.value)
      } finally {
        loading.value = false
      }
    }

    const loadCourse = async () => {
      try {
        const response = await lmsAPI.getCourse(props.courseId)
        course.value = response.data
        
        // Check if course has quiz
        courseHasQuiz.value = course.value.quiz_questions && course.value.quiz_questions.length > 0
        
        // Check quiz status from user_progress
        if (course.value.user_progress?.completion_criteria) {
          quizPassed.value = course.value.user_progress.completion_criteria.quiz_passed || false
        }
        
        // Load saved research for this course
        loadSavedResearch()
      } catch (err) {
        console.error('Error loading course:', err)
      }
    }
    
    const loadSavedResearch = async () => {
      loadingSavedResearch.value = true
      try {
        const response = await lmsAPI.getResourcesForCourse(props.courseId)
        savedCourseResources.value = response.data.my_resources || []
      } catch (err) {
        console.error('Error loading saved research:', err)
        // Non-critical - don't show error to user
      } finally {
        loadingSavedResearch.value = false
      }
    }
    
    const toggleResourceFavorite = async (resource) => {
      try {
        await lmsAPI.toggleResourceFavorite(resource.id)
        resource.is_favorite = !resource.is_favorite
      } catch (err) {
        console.error('Error toggling favorite:', err)
      }
    }
    
    const truncateTitle = (title, maxLength) => {
      if (!title || title.length <= maxLength) return title
      return title.substring(0, maxLength) + '...'
    }
    
    // Load enrollment progress for the current course
    const loadEnrollmentProgress = async () => {
      try {
        const response = await lmsAPI.getCourseProgress(props.courseId)
        enrollment.value = response.data.enrollment
        
        // Find progress for current video
        const videoProgressList = response.data.video_progress || []
        videoProgress.value = videoProgressList.find(vp => vp.video == props.videoId)
        
        if (videoProgress.value) {
          lastPositionSeconds.value = videoProgress.value.last_position_seconds || 0
          // Initialize maxPositionSeconds to the loaded value - this is our starting max
          maxPositionSeconds.value = lastPositionSeconds.value
          console.log('📍 Found saved progress:', lastPositionSeconds.value, 'seconds (set as initial max)')
        }
        
        // Check if all videos are completed
        const totalVideos = courseVideos.value.length
        const completedVideos = videoProgressList.filter(vp => vp.is_completed).length
        allVideosCompleted.value = totalVideos > 0 && completedVideos >= totalVideos
        
        // Show quiz prompt if all videos done but quiz not passed
        if (allVideosCompleted.value && courseHasQuiz.value && !quizPassed.value) {
          showQuizPrompt.value = true
        }
        
        console.log('📊 Progress loaded:', {
          totalVideos,
          completedVideos,
          allVideosCompleted: allVideosCompleted.value,
          courseHasQuiz: courseHasQuiz.value,
          quizPassed: quizPassed.value,
          lastPositionSeconds: lastPositionSeconds.value
        })
        
        // Mark progress as loaded - triggers restore if player is already ready
        progressLoaded.value = true
        
        // If Vimeo player is already ready, restore progress now
        if (vimeoPlayerReady.value && lastPositionSeconds.value > 0) {
          console.log('🔄 Player already ready, restoring progress now')
          await restoreVimeoProgress()
        }
      } catch (err) {
        console.log('No enrollment progress found (user may not be enrolled)')
        progressLoaded.value = true  // Mark as loaded even on error
      }
    }
    
    // Restore Vimeo player position - uses seekToTimestamp which is known to work
    const restoreVimeoProgress = async () => {
      if (lastPositionSeconds.value <= 0 || progressRestored.value) {
        return
      }
      
      progressRestored.value = true
      console.log('📍 Restoring progress to', lastPositionSeconds.value, 'seconds via seekToTimestamp')
      seekToTimestamp(lastPositionSeconds.value)
    }

    const loadCourseVideos = async () => {
      loadingCourseVideos.value = true
      try {
        const response = await lmsAPI.getVideos({ course: props.courseId })
        courseVideos.value = response.data.results || response.data
      } catch (err) {
        console.error('Error loading course videos:', err)
      } finally {
        loadingCourseVideos.value = false
      }
    }

    const handleTimeUpdate = (event) => {
      currentTime.value = event.target.currentTime
      if (video.value && video.value.duration_seconds) {
        progressPercentage.value = (currentTime.value / video.value.duration_seconds) * 100
      }
      if (previewMode.value && previewEnd.value > 0 && currentTime.value >= previewEnd.value) {
        event.target.currentTime = Math.max(previewStart.value, previewEnd.value - 1)
        event.target.pause()
        previewPaused.value = true
      }
    }

    const handlePlay = () => {
      // Start periodic progress updates
      startProgressTracking()
    }

    const handlePause = () => {
      // Save progress on pause
      saveProgress()
    }

    const handleVideoEnded = () => {
      // Mark video as completed
      saveProgress(true)
      
      // Auto-play next video after a delay
      if (nextVideo.value) {
        setTimeout(() => {
          toast.info(`Starting next video: ${nextVideo.value.title}`)
          playNextVideo()
        }, 2000)
      } else {
        // This is the last video - check if quiz is needed
        allVideosCompleted.value = true
        
        if (courseHasQuiz.value && !quizPassed.value) {
          showQuizPrompt.value = true
          toast.success('🎉 All videos completed! Click here to take the quiz.', {
            timeout: 8000,
            onClick: () => router.push(`/learning/courses/${props.courseId}/quiz`)
          })
        } else if (!courseHasQuiz.value) {
          toast.success('🎉 Course completed! Great job!', {
            timeout: 5000,
            onClick: () => router.push(`/learning/courses/${props.courseId}`)
          })
        } else {
          toast.success('🎉 Course completed! Click here to download your certificate.', {
            timeout: 5000,
            onClick: () => router.push(`/learning/courses/${props.courseId}`)
          })
        }
      }
    }

    const startProgressTracking = () => {
      if (progressUpdateInterval.value) return

      // Update progress every 30 seconds
      progressUpdateInterval.value = setInterval(() => {
        saveProgress()
      }, 30000)
    }

    const saveProgress = async (completed = false) => {
      // Allow saving for both HTML5 video and Vimeo players
      if (!video.value || (!videoPlayer.value && !vimeoPlayer.value)) return
      
      const currentPosition = Math.floor(currentTime.value)
      
      // Skip if no valid position yet
      if (currentPosition <= 0) {
        console.log('⏭️ Skipping save - no playback position yet')
        return
      }
      
      // Update maxPositionSeconds if user has progressed further
      // This tracks the HIGHEST point they've reached in this session
      if (currentPosition > maxPositionSeconds.value) {
        maxPositionSeconds.value = currentPosition
        console.log('📈 New max position:', maxPositionSeconds.value, 'seconds')
      }
      
      // Always send the MAX position to the backend, not current position
      // This way even if user is rewatching earlier content, we preserve their best progress
      // The backend also has this protection, but we optimize by sending max from frontend too
      const positionToSave = maxPositionSeconds.value
      
      // Don't save if our max position is less than what was originally loaded
      // This prevents edge cases where we somehow have a lower max
      if (lastPositionSeconds.value > 0 && positionToSave < lastPositionSeconds.value) {
        console.log('⏭️ Skipping save - max position', positionToSave, 'is less than loaded position', lastPositionSeconds.value)
        return
      }

      try {
        // Use maxProgressPercentage for completion check - this is the HIGHEST progress achieved
        // This ensures we correctly mark as complete even if user rewatches earlier parts
        const isVideoComplete = completed || maxProgressPercentage.value >= 95
        
        await lmsAPI.updateVideoProgress(props.courseId, {
          video_id: props.videoId,
          position_seconds: positionToSave,
          watch_time_seconds: positionToSave,
          is_completed: isVideoComplete
        })
        console.log('💾 Progress saved:', {
          currentPosition: currentPosition,
          savedPosition: positionToSave,
          currentPercentage: progressPercentage.value.toFixed(1),
          maxPercentage: maxProgressPercentage.value.toFixed(1),
          completed: isVideoComplete
        })
      } catch (err) {
        console.error('Error saving progress:', err)
      }
    }

    const seekToTimestamp = async (seconds) => {
      console.log('🎯 Seeking to timestamp:', seconds, 'seconds')
      
      // Update local state immediately so progress saves reflect the new position
      currentTime.value = seconds
      if (seconds > maxPositionSeconds.value) {
        maxPositionSeconds.value = seconds
        console.log('📈 Updated max position locally for seek:', maxPositionSeconds.value)
      }

      // For HTML5 video elements
      if (videoPlayer.value) {
        videoPlayer.value.currentTime = seconds
        videoPlayer.value.play()
        console.log('✅ Seeked HTML5 video to', seconds)
      } 
      // For Vimeo embeds - update reactive timestamp to trigger iframe reload
      else if (isVimeoVideo.value) {
        console.log('🎬 Vimeo video detected, updating reactive timestamp for reload')
        
        // This triggers the vimeoEmbedUrl computed property change, 
        // which Vue then reflects in the iframe :src and :key
        vimeoSeekTimestamp.value = seconds
        
        console.log('✅ Vimeo seek initiated via reactive URL update')
        toast.success(`Jumped to ${formatTime(seconds)}`)
      }
      // For YouTube embeds
      else if (isYouTubeVideo.value && video.value) {
        console.log('📺 YouTube video detected, reloading with timestamp')
        const youtubeEmbedUrl = getYouTubeEmbedUrl()
        const urlWithTime = `${youtubeEmbedUrl}&start=${Math.floor(seconds)}`
        
        const iframe = document.querySelector('.video-element')
        if (iframe) {
          iframe.src = urlWithTime
          console.log('✅ YouTube iframe reloaded with time:', urlWithTime)
        }
      }
      else {
        console.warn('⚠️ No video player reference found')
      }
    }

    // Alias for POI component
    const seekToTime = seekToTimestamp

    const isTimestampActive = (timestamp) => {
      if (!timestamps.value.length) return false
      const currentIndex = timestamps.value.findIndex(t => t.time_seconds <= currentTime.value)
      const nextIndex = timestamps.value.findIndex(t => t.time_seconds > currentTime.value)
      
      if (nextIndex === -1) {
        return timestamp === timestamps.value[timestamps.value.length - 1]
      }
      
      return timestamp.time_seconds <= currentTime.value && 
             timestamp.time_seconds >= (timestamps.value[currentIndex]?.time_seconds || 0)
    }

    const navigateToVideo = (videoId) => {
      router.push({
        name: 'CourseVideoPlayer',
        params: {
          courseId: props.courseId,
          videoId: videoId
        }
      })
    }

    const playNextVideo = () => {
      if (nextVideo.value) {
        navigateToVideo(nextVideo.value.id)
      }
    }

    const formatDuration = (seconds) => {
      return lmsAPI.formatDuration(seconds)
    }

    const formatTime = (seconds) => {
      return lmsAPI.formatDuration(seconds)
    }

    const getVideoUrl = (videoFile) => {
      if (!videoFile) return ''
      if (videoFile.startsWith('http')) return videoFile
      return `${process.env.VUE_APP_API_URL || ''}/media/${videoFile}`
    }

    // Watch for route changes (when navigating between videos)
    watch(() => props.videoId, async (newId, oldId) => {
      if (newId !== oldId) {
        // Save progress of previous video
        if (oldId) {
          saveProgress()
        }
        // Cleanup old Vimeo player
        if (vimeoPlayer.value) {
          try {
            vimeoPlayer.value.destroy()
          } catch (err) {
            console.log('Error destroying old player:', err)
          }
          vimeoPlayer.value = null
          vimeoPlayerReady.value = false
          vimeoVideoTitle.value = null
        }
        // Reset progress restore flag for new video
        progressRestored.value = false
        progressLoaded.value = false
        lastPositionSeconds.value = 0
        maxPositionSeconds.value = 0  // Reset max position for new video
        // Load new video
        await loadVideo()
        // Reset player
        currentTime.value = 0
        progressPercentage.value = 0
      }
    })

    // Load cached analysis on mount
    const loadCachedAnalysis = async () => {
      if (!video.value || !video.value.video_url) return

      try {
        const api = (await import('@/services/api.js')).default
        const response = await api.get(`/lms/videos/${props.videoId}/get_cached_analysis/`)
        
        if (response.data.success) {
          transcriptAnalysis.value = response.data
          smartResearchResults.value = response.data.pubmed_results || []
          console.log('Loaded cached transcript analysis')
        }
      } catch (err) {
        // No cached analysis available - that's fine
        console.log('No cached analysis available')
      }
    }

    onMounted(async () => {
      // Load course first to get quiz info
      await loadCourse()
      
      // Load videos and enrollment progress in parallel
      await Promise.all([
        loadVideo(),
        loadCourseVideos(),
        loadAPIConnections(),
        loadEnrollmentProgress()
      ])
      
      // Load cached analysis after video loads
      await loadCachedAnalysis()

      // Auto-seek + paywall: ?seek=<seconds>[&preview=1][&end=<seconds>]
      const seekParam = route.query.seek
      const seekSeconds = seekParam != null ? parseInt(seekParam, 10) : 0
      const endParam = route.query.end
      const endSeconds = endParam != null ? parseInt(endParam, 10) : null
      const parsedEnd = Number.isNaN(endSeconds) ? null : endSeconds
      const wantsPreview = route.query.preview === '1' || !video.value?.video_url

      if (wantsPreview && !Number.isNaN(seekSeconds) && seekSeconds >= 0) {
        const result = await applyPreviewAccess(seekSeconds, parsedEnd)
        if (result?.enrolled && seekParam) {
          setTimeout(() => { seekToTimestamp(seekSeconds) }, 1500)
        } else if (result && !result.enrolled) {
          setTimeout(() => { seekToTimestamp(result.startAt) }, 1500)
        }
      } else if (seekParam) {
        const parsedSeek = parseInt(seekParam, 10)
        if (!Number.isNaN(parsedSeek) && parsedSeek >= 0) {
          setTimeout(() => { seekToTimestamp(parsedSeek) }, 1500)
        }
      }
    })

    // Load available API connections
    const loadAPIConnections = async () => {
      try {
        const api = (await import('@/services/api.js')).default
        const response = await api.get('/api-connections/')
        const connections = response.data.results || response.data
        
        // Filter to only show NCBI/scientific database connections
        availableConnections.value = connections.filter(c => 
          c.api_template && (
            c.api_template.api_type === 'NCBI_EUTILS' ||
            c.name.toLowerCase().includes('pubmed') ||
            c.name.toLowerCase().includes('ncbi')
          )
        )
        
        // Auto-select all connections by default
        selectedConnections.value = availableConnections.value.map(c => c.id)
      } catch (err) {
        console.error('Error loading API connections:', err)
      }
    }

    // Research functions
    const performResearch = async () => {
      if (!researchQuery.value.trim()) return
      
      if (selectedConnections.value.length === 0) {
        toast.error('Please select at least one database to search.')
        return
      }

      loadingResearch.value = true
      researchSearched.value = true
      researchResults.value = []

      try {
        const results = []
        
        // Search each selected connection
        for (const connectionId of selectedConnections.value) {
          try {
            // Import API service dynamically
            const api = (await import('@/services/api.js')).default
            
            // Search using this connection
            const searchResponse = await api.post(`/api-connections/${connectionId}/ncbi_search/`, {
              database: 'pubmed',
              query: researchQuery.value,
              max_results: 10
            })

            console.log('Search Response:', searchResponse.data)

            // Handle different response formats
            let idList = []
            if (searchResponse.data) {
              // Check for direct id_list
              if (searchResponse.data.id_list) {
                idList = searchResponse.data.id_list
              }
              // Check for nested esearchresult format
              else if (searchResponse.data.data && searchResponse.data.data.esearchresult) {
                idList = searchResponse.data.data.esearchresult.idlist || []
              }
              // Check for direct esearchresult format
              else if (searchResponse.data.esearchresult) {
                idList = searchResponse.data.esearchresult.idlist || []
              }
            }

            console.log('ID List:', idList)

            if (idList && idList.length > 0) {
              // Get summaries for the results (returns JSON, not XML)
              try {
                const summariesResponse = await api.post(`/api-connections/${connectionId}/ncbi_get_summaries/`, {
                  pmids: idList
                })

                console.log('Summaries Response:', summariesResponse.data)
                console.log('Full Summaries Response:', JSON.stringify(summariesResponse.data, null, 2))

                // Handle summaries response format
                let summaries = []
                if (summariesResponse.data && summariesResponse.data.data) {
                  const data = summariesResponse.data.data
                  
                  // ESummary returns result object with PMID keys
                  if (data.result) {
                    // Get UIDs list
                    const uids = data.result.uids || idList
                    
                    uids.forEach(pmid => {
                      const summary = data.result[pmid]
                      if (summary && summary.title) {
                        summaries.push(summary)
                      }
                    })
                  }
                }

                console.log('Parsed Summaries:', summaries)

                if (summaries.length > 0) {
                  summaries.forEach(summary => {
                    results.push({
                      title: summary.title || 'Untitled',
                      abstract: summary.abstract || summary.snippet || 'No abstract available',
                      authors: summary.authors ? (Array.isArray(summary.authors) ? summary.authors.map(a => a.name || a).join(', ') : summary.authors) : (summary.sortfirstauthor || ''),
                      year: summary.pubdate ? summary.pubdate.split(' ')[0] : (summary.epubdate || ''),
                      journal: summary.fulljournalname || summary.source || '',
                      url: summary.uid ? `https://pubmed.ncbi.nlm.nih.gov/${summary.uid}/` : '',
                      source: 'PubMed'
                    })
                  })
                }
                
                console.log('Final Results:', results)
              } catch (summariesErr) {
                console.error('Error fetching summaries:', summariesErr)
                // If abstracts fail, still show results without abstracts
                idList.forEach((pmid) => {
                  results.push({
                    title: `Article PMID: ${pmid}`,
                    abstract: 'Abstract not available',
                    authors: '',
                    year: '',
                    journal: '',
                    url: `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`,
                    source: 'PubMed'
                  })
                })
              }
            }
          } catch (err) {
            console.error(`Error searching connection ${connectionId}:`, err)
            // Continue to next connection instead of stopping
          }
        }

        researchResults.value = results

        if (results.length === 0) {
          toast.info('No results found. Try different keywords.')
        } else {
          toast.success(`Found ${results.length} research articles`)
          
          // Expand Smart Research section if collapsed
          showSmartResearch.value = true
          
          // Scroll to results after they render
          setTimeout(() => {
            const resultsElement = document.querySelector('.manual-search-results')
            if (resultsElement) {
              resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          }, 100)
        }
      } catch (err) {
        console.error('Error performing research:', err)
        toast.error('Failed to search databases')
      } finally {
        loadingResearch.value = false
      }
    }

    const quickSearch = (query) => {
      researchQuery.value = query
      performResearch()
    }

    // Smart Research functions
    const analyzeTranscript = async () => {
      if (!video.value || !video.value.video_url) {
        toast.error('Video must have a Vimeo URL for transcript analysis')
        return
      }

      analyzingTranscript.value = true

      try {
        const api = (await import('@/services/api.js')).default
        
        // Try to analyze without token first (backend will use organization's token)
        try {
          const response = await api.post(`/lms/videos/${props.videoId}/analyze_transcript/`, {})
          
          transcriptAnalysis.value = response.data
          smartResearchResults.value = response.data.pubmed_results || []
          
          const totalTerms = response.data.medical_terms?.total_terms_found || 
                            response.data.keywords?.length || 0
          toast.success(`✨ Found ${totalTerms} enhanced medical keywords!`)
          
          // Show extraction method breakdown
          if (response.data.extraction_methods) {
            const methods = response.data.extraction_methods
            console.log('📊 Keyword extraction breakdown:', methods)
          }
          
        } catch (err) {
          // If backend says token is required, prompt for it
          if (err.response?.status === 400 && err.response?.data?.error?.includes('token')) {
            const vimeoToken = prompt('Enter your Vimeo access token (needed to access video transcript):')
            
            if (!vimeoToken) {
              toast.info('Vimeo access token required to analyze transcript')
              return
            }
            
            // Retry with provided token
            const response = await api.post(`/lms/videos/${props.videoId}/analyze_transcript/`, {
              vimeo_access_token: vimeoToken
            })
            
            transcriptAnalysis.value = response.data
            smartResearchResults.value = response.data.pubmed_results || []
            
            const totalTerms = response.data.medical_terms?.total_terms_found || 
                              response.data.keywords?.length || 0
            toast.success(`✨ Found ${totalTerms} enhanced medical keywords!`)
          } else {
            throw err
          }
        }
      } catch (err) {
        console.error('Error analyzing transcript:', err)
        const errorMsg = err.response?.data?.error || 'Failed to analyze video transcript'
        toast.error(errorMsg)
      } finally {
        analyzingTranscript.value = false
      }
    }

    const seekToTerm = (term) => {
      if (term.timestamps && term.timestamps.length > 0 && videoPlayer.value) {
        // Jump to first occurrence of the term
        const firstOccurrence = term.timestamps[0]
        videoPlayer.value.currentTime = firstOccurrence.start
        videoPlayer.value.play()
        toast.info(`Jumping to: ${term.term}`)
      }
    }

    const getArticlesFromResult = (articlesData) => {
      // Parse the articles from the esummary result format
      if (!articlesData || !articlesData.result) return []

      const result = articlesData.result
      const uids = result.uids || []

      return uids.map(uid => {
        const article = result[uid]
        if (!article) return null

        return {
          title: article.title || 'Untitled',
          abstract: article.snippet || article.elocationid || 'No abstract available',
          authors: article.sortfirstauthor || article.authors?.[0]?.name || '',
          journal: article.fulljournalname || article.source || '',
          year: article.pubdate ? article.pubdate.split(' ')[0] : '',
          url: uid ? `https://pubmed.ncbi.nlm.nih.gov/${uid}/` : ''
        }
      }).filter(article => article !== null)
    }

    // Note: We no longer need to watch vimeoEmbedUrl for cleanup.
    // The iframe's @load handler (handleVimeoIframeLoad) handles player 
    // cleanup and re-initialization whenever the URL/src changes.
    
    onBeforeUnmount(() => {
      // Save progress before leaving
      saveProgress()
      // Clear interval
      if (progressUpdateInterval.value) {
        clearInterval(progressUpdateInterval.value)
      }
      // Cleanup Vimeo player
      if (vimeoPlayer.value) {
        try {
          vimeoPlayer.value.destroy()
        } catch (err) {
          console.log('Error destroying player on unmount:', err)
        }
        vimeoPlayer.value = null
        vimeoPlayerReady.value = false
      }
    })

    return {
      video,
      course,
      timestamps,
      resources,
      courseVideos,
      loading,
      loadingCourseVideos,
      error,
      videoPlayer,
      currentTime,
      progressPercentage,
      nextVideo,
      returnToConsultLink,
      previewDurationSeconds,
      dismissPreviewUpsell,
      isVimeoVideo,
      isYouTubeVideo,
      vimeoEmbedUrl,
      getVimeoEmbedUrl,
      handleVimeoIframeLoad,
      getYouTubeEmbedUrl,
      loadVideo,
      handleTimeUpdate,
      handlePlay,
      handlePause,
      handleVideoEnded,
      seekToTimestamp,
      seekToTime,
      isTimestampActive,
      navigateToVideo,
      playNextVideo,
      formatDuration,
      formatTime,
      getVideoUrl,
      // Quiz and progress state
      enrollment,
      videoProgress,
      showQuizPrompt,
      courseHasQuiz,
      quizPassed,
      allVideosCompleted,
      // Research section
      researchQuery,
      researchResults,
      loadingResearch,
      researchSearched,
      availableConnections,
      selectedConnections,
      performResearch,
      quickSearch,
      // Smart Research
      transcriptAnalysis,
      smartResearchResults,
      analyzingTranscript,
      analyzeTranscript,
      seekToTerm,
      getArticlesFromResult,
      // UI State
      showTopics,
      showSmartResearch,
      // Vimeo metadata
      vimeoIframeRef,
      vimeoPlayerReady,
      vimeoVideoTitle,
      // Saved Research for Course
      savedCourseResources,
      loadingSavedResearch,
      loadSavedResearch,
      toggleResourceFavorite,
      truncateTitle,
      // Phase 3 — Preview / paywall
      previewMode,
      previewPaused,
      previewEnd,
      upsellData,
    }
  }
}
</script>

<style scoped>
/* ── Preview / Paywall Upsell Overlay (Phase 3) ────────────────────── */
.preview-upsell-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
  border-radius: 6px;
}

.upsell-card {
  background: #1a1a2e;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 14px;
  padding: 2.5rem 2.5rem 2rem;
  max-width: 420px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6);
}

.upsell-icon {
  font-size: 2.5rem;
  color: #f59e0b;
  margin-bottom: 1rem;
}

.upsell-card h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.75rem;
}

.upsell-card p {
  color: rgba(255,255,255,0.75);
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0 0 0.5rem;
}

.upsell-sub {
  font-size: 0.85rem !important;
  color: rgba(255,255,255,0.5) !important;
}

.upsell-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.btn-upsell-enroll {
  display: block;
  background: linear-gradient(135deg, #00a79d 0%, #007a73 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.85rem 1.5rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s;
}
.btn-upsell-enroll:hover { opacity: 0.9; }

.btn-upsell-dismiss {
  background: transparent;
  color: rgba(255,255,255,0.5);
  border: none;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.5rem;
}
.btn-upsell-dismiss:hover { color: rgba(255,255,255,0.8); }

.video-player-page {
  min-height: 100vh;
  background: #000;
  color: white;
}

/* Loading & Error States */
.loading-container,
.error-container {
  text-align: center;
  padding: var(--spacing-xxl);
  background: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left-color: var(--color-primary);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-md);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-container i {
  font-size: 3rem;
  color: #ff6b6b;
  margin-bottom: var(--spacing-md);
}

/* Breadcrumb */
.breadcrumb {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: rgba(0, 0, 0, 0.9);
  font-size: 0.9rem;
}

/* Collapsible Sections */
.collapsible-section {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.section-header:hover {
  background: rgba(255, 255, 255, 0.08);
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-header i.fa-chevron-up,
.section-header i.fa-chevron-down {
  font-size: 14px;
  color: #999;
}

.smart-research-header-content {
  flex: 1;
}

.timestamps-list .play-icon {
  color: var(--color-primary, #00a79d);
  font-size: 20px;
  margin-left: auto;
  opacity: 0;
  transition: opacity 0.2s;
}

.timestamp-item:hover .play-icon {
  opacity: 1;
}

.manual-research-section {
  margin: 24px 0;
  padding: 20px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
}

.breadcrumb a {
  color: #aaa;
  text-decoration: none;
}

.breadcrumb a:hover {
  color: white;
  text-decoration: underline;
}

.breadcrumb .separator {
  color: #666;
}

.breadcrumb .current {
  color: white;
}

/* Quick Navigation Bar */
.quick-nav-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-lg);
  background: linear-gradient(135deg, rgba(0, 167, 157, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.nav-btn.back-btn,
.nav-btn.back-consult-btn {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.nav-btn.back-btn:hover,
.nav-btn.back-consult-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-2px);
}

.nav-btn.back-consult-btn {
  background: rgba(14, 165, 233, 0.25);
  border-color: rgba(14, 165, 233, 0.45);
}

.nav-btn.quiz-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.nav-btn.quiz-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.nav-center {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.video-progress-indicator {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.video-progress-indicator i {
  color: var(--color-primary);
}

.quiz-passed-badge {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(16, 185, 129, 0.2);
  border: 1px solid rgba(16, 185, 129, 0.4);
  border-radius: 6px;
  color: #10b981;
  font-weight: 600;
  font-size: 0.9rem;
}

/* Player Content */
.player-content {
  background: #000;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* NEW: 3-Column Grid Layout */
.player-content-grid {
  display: grid;
  grid-template-columns: 300px 1fr 340px;
  grid-template-rows: 1fr;
  gap: 20px;
  padding: 20px;
  flex: 1;
  background: var(--color-background, #F9FAFB);
}

.materials-panel {
  grid-column: 1;
  grid-row: 1;
  overflow-y: auto;
}

.video-section-center {
  grid-column: 2;
  grid-row: 1;
  display: flex;
  flex-direction: column;
}

.poi-panel {
  grid-column: 3;
  grid-row: 1;
  overflow-y: auto;
}

.topics-panel {
  grid-column: 3;
  grid-row: 1;
  background: var(--color-surface, #fff);
  border-radius: 8px;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.topics-panel .panel-header {
  margin-bottom: 16px;
}

.topics-panel .panel-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text, #111);
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.topics-panel .panel-subtitle {
  font-size: 13px;
  color: var(--color-text-light, #666);
  margin: 0;
}

.topics-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.topic-item {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--color-surface, #fff);
  border: 2px solid var(--color-border, #e0e0e0);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.topic-item:hover {
  background: rgba(0, 167, 157, 0.05);
  border-left-color: var(--color-primary, #00a79d);
  transform: translateX(-4px);
  box-shadow: 0 2px 8px rgba(0, 167, 157, 0.1);
}

.topic-item.active {
  background: rgba(0, 167, 157, 0.1);
  border-left-color: var(--color-primary, #00a79d);
  border-color: var(--color-primary, #00a79d);
}

.topic-time {
  font-weight: 600;
  color: var(--color-primary, #00a79d);
  font-family: monospace;
  font-size: 0.9rem;
  min-width: 60px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.topic-content {
  flex: 1;
  min-width: 0;
}

.topic-content h4 {
  margin: 0 0 4px 0;
  color: var(--color-text, #111);
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.3;
}

.topic-content p {
  margin: 0;
  color: var(--color-text-light, #666);
  font-size: 0.85rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.topic-item .play-icon {
  color: var(--color-primary, #00a79d);
  font-size: 20px;
  opacity: 0;
  transition: opacity 0.2s;
}

.topic-item:hover .play-icon {
  opacity: 1;
}

.topics-empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-light, #666);
}

.topics-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.topics-empty p {
  margin: 0;
  font-size: 14px;
}

/* Saved Research Panel */
.saved-research-panel {
  margin-top: var(--spacing-lg);
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--color-border, #e0e0e0);
}

.saved-research-header h3 {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: #10b981;
}

.saved-resources-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.saved-resource-item {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm);
  background: var(--color-surface-hover, #f8f9fa);
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;
}

.saved-resource-item:hover {
  background: var(--color-surface-active, #e9ecef);
}

.saved-resource-content {
  flex: 1;
  min-width: 0;
}

.saved-resource-title {
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-text-primary, #333);
  text-decoration: none;
  line-height: 1.4;
  margin-bottom: 4px;
}

.saved-resource-title:hover {
  color: var(--color-primary, #00a79d);
}

.saved-resource-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  font-size: 0.75rem;
  color: var(--color-text-secondary, #666);
}

.saved-resource-meta span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.btn-icon-mini {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 0.85rem;
  color: var(--color-text-secondary, #999);
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-icon-mini:hover {
  color: #f59e0b;
  transform: scale(1.1);
}

.btn-icon-mini.is-favorite {
  color: #f59e0b;
}

.view-all-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm);
  margin-top: var(--spacing-sm);
  font-size: 0.85rem;
  color: var(--color-primary, #00a79d);
  text-decoration: none;
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;
}

.view-all-link:hover {
  background: rgba(0, 167, 157, 0.1);
}

.saved-research-empty {
  text-align: center;
  padding: var(--spacing-lg);
  color: var(--color-text-secondary, #666);
}

.saved-research-empty i {
  font-size: 2rem;
  margin-bottom: var(--spacing-sm);
  opacity: 0.5;
  color: #10b981;
}

.saved-research-empty p {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
}

.loading-mini {
  display: flex;
  justify-content: center;
  padding: var(--spacing-lg);
}

.research-bar {
  background: var(--color-surface, #fff);
  border-top: 1px solid var(--color-border, #e0e0e0);
}

/* Responsive Grid */
@media (max-width: 1400px) {
  .player-content-grid {
    grid-template-columns: 260px 1fr 300px;
  }
}

@media (max-width: 1200px) {
  .player-content-grid {
    grid-template-columns: 240px 1fr 280px;
  }
}

@media (max-width: 992px) {
  .player-content-grid {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr auto;
  }
  
  .materials-panel {
    grid-row: 1;
    grid-column: 1;
    max-height: 200px;
  }
  
  .video-section-center {
    grid-row: 2;
    grid-column: 1;
  }
  
  .poi-panel,
  .topics-panel {
    grid-row: 3;
    grid-column: 1;
    max-height: 300px;
  }
}

.video-section {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0;
}

.video-container {
  background: #0a0a0a;
  padding: var(--spacing-lg);
}

/* Video Title Above Player */
.video-title-top {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 var(--spacing-sm) 0;
  padding: var(--spacing-md) var(--spacing-lg);
  color: white;
  text-align: left;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 8px 8px 0 0;
}

.video-subtitle {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 var(--spacing-lg) 0;
  padding: 0 var(--spacing-lg);
  font-weight: 400;
}

/* Video Player */
.video-wrapper {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto var(--spacing-lg);
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.video-element {
  width: 100%;
  height: auto;
  display: block;
  aspect-ratio: 16 / 9;
}

/* Video Info */
.video-info {
  max-width: 1200px;
  margin: 0 auto var(--spacing-xl);
  padding: var(--spacing-md) 0;
}

.video-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 var(--spacing-md) 0;
  color: white;
}

.video-meta {
  display: flex;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  flex-wrap: wrap;
  font-size: 0.95rem;
  color: #aaa;
}

.video-meta span {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.video-meta i {
  color: var(--color-primary);
}

.progress-badge {
  color: #4caf50;
  font-weight: 600;
}

.video-description {
  line-height: 1.6;
  color: #ccc;
  margin: 0;
}

/* Timestamps */
.timestamps-section,
.resources-section {
  max-width: 1200px;
  margin: 0 auto var(--spacing-xl);
  padding: var(--spacing-lg);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.timestamps-section h3,
.resources-section h3 {
  font-size: 1.2rem;
  margin: 0 0 var(--spacing-md) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: white;
}

.timestamps-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.timestamp-item {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.timestamp-item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-left-color: var(--color-primary);
}

.timestamp-item.active {
  background: rgba(var(--color-primary-rgb), 0.2);
  border-left-color: var(--color-primary);
}

.timestamp-time {
  font-weight: 600;
  color: var(--color-primary);
  font-family: monospace;
  font-size: 0.95rem;
  min-width: 60px;
}

.timestamp-content h4 {
  margin: 0 0 var(--spacing-xs) 0;
  color: white;
  font-size: 1rem;
}

.timestamp-content p {
  margin: 0;
  color: #aaa;
  font-size: 0.9rem;
}

/* Resources */
.resources-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.resource-item {
  display: flex;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  text-decoration: none;
  color: white;
  transition: all 0.2s ease;
}

.resource-item:hover {
  background: rgba(255, 255, 255, 0.1);
  transform: translateX(4px);
}

.resource-item i {
  color: var(--color-primary);
  margin-top: 4px;
}

.resource-content h4 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 1rem;
}

.resource-content p {
  margin: 0 0 var(--spacing-xs) 0;
  color: #aaa;
  font-size: 0.9rem;
}

.resource-type {
  display: inline-block;
  padding: 2px 8px;
  background: rgba(var(--color-primary-rgb), 0.2);
  border-radius: 4px;
  font-size: 0.75rem;
  color: var(--color-primary);
  text-transform: uppercase;
}

/* Sidebar */
.course-sidebar {
  background: #1a1a1a;
  border-left: 1px solid #333;
  overflow-y: auto;
  max-height: calc(100vh - 60px);
}

.sidebar-header {
  padding: var(--spacing-lg);
  border-bottom: 1px solid #333;
  position: sticky;
  top: 0;
  background: #1a1a1a;
  z-index: 10;
}

.sidebar-header h2 {
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 1.2rem;
  color: white;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  color: var(--color-primary);
  text-decoration: none;
  font-size: 0.9rem;
}

.back-link:hover {
  text-decoration: underline;
}

/* Video Playlist */
.video-playlist {
  padding: var(--spacing-md);
}

.playlist-item {
  display: flex;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: var(--spacing-sm);
  align-items: center;
  border-left: 3px solid transparent;
}

.playlist-item:hover {
  background: rgba(255, 255, 255, 0.05);
}

.playlist-item.active {
  background: rgba(var(--color-primary-rgb), 0.2);
  border-left-color: var(--color-primary);
}

.playlist-item.completed {
  opacity: 0.7;
}

.playlist-index {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.playlist-item.active .playlist-index {
  background: var(--color-primary);
}

.playlist-info {
  flex: 1;
  min-width: 0;
}

.playlist-info h4 {
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 0.95rem;
  color: white;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.playlist-meta {
  display: flex;
  gap: var(--spacing-sm);
  font-size: 0.8rem;
  color: #aaa;
}

.completed-check {
  color: #4caf50;
}

.active-icon {
  color: var(--color-primary);
  font-size: 1.5rem;
}

/* Next Video */
.next-video-section {
  padding: var(--spacing-lg);
  border-top: 1px solid #333;
}

.btn-block {
  width: 100%;
  justify-content: center;
}

/* Loading Small */
.loading-small {
  text-align: center;
  padding: var(--spacing-lg);
}

.spinner-small {
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-left-color: var(--color-primary);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin: 0 auto var(--spacing-sm);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: 6px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--color-secondary);
  transform: translateY(-2px);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Smart Research Section */
.analyze-prompt-section,
.smart-research-section {
  max-width: 1200px;
  margin: var(--spacing-xxl) auto;
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%);
  border-radius: 12px;
  border: 2px solid rgba(16, 185, 129, 0.3);
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.1);
}

.analyze-prompt-section {
  text-align: center;
}

.smart-research-header-placeholder {
  margin-bottom: var(--spacing-lg);
}

.smart-research-header-placeholder h3 {
  font-size: 1.8rem;
  margin: 0 0 var(--spacing-sm) 0;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
}

.analyze-prompt-section .btn {
  margin: var(--spacing-lg) 0;
  padding: var(--spacing-md) var(--spacing-xl);
  font-size: 1.1rem;
}

.analyze-info {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-top: var(--spacing-md);
}

.smart-research-header {
  margin-bottom: var(--spacing-lg);
}

.smart-research-header h3 {
  font-size: 1.8rem;
  margin: 0 0 var(--spacing-sm) 0;
  color: white;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.smart-research-header .badge {
  font-size: 0.75rem;
  padding: 4px 12px;
  border-radius: 12px;
}

.badge-success {
  background: rgba(16, 185, 129, 0.2);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.4);
}

.smart-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
}

/* Medical Terms Timeline */
.medical-terms-timeline {
  margin: var(--spacing-xl) 0;
  padding: var(--spacing-lg);
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  border-left: 4px solid #10b981;
}

.medical-terms-timeline h4 {
  color: white;
  margin: 0 0 var(--spacing-md) 0;
  font-size: 1.2rem;
}

.terms-help-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin: var(--spacing-sm) 0 var(--spacing-md) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.terms-help-text i {
  color: #10b981;
}

.terms-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--spacing-md);
}

.term-card {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.term-card:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.term-card.term-procedure {
  border-left: 4px solid #ef4444;
}

.term-card.term-condition {
  border-left: 4px solid #f59e0b;
}

.term-card.term-anatomy {
  border-left: 4px solid #3b82f6;
}

.term-card.term-diagnostic {
  border-left: 4px solid #8b5cf6;
}

.term-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-sm);
}

.term-name {
  font-weight: 600;
  color: white;
  font-size: 1rem;
  flex: 1;
}

.term-badges {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}

.importance-badge,
.source-badge,
.mention-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
}

.importance-badge.high {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #000;
}

.source-badge.llm {
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
}

.source-badge.chapter {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.mention-badge {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
}

.term-context {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  padding: var(--spacing-xs);
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  border-left: 2px solid rgba(139, 92, 246, 0.5);
  font-style: italic;
}

.term-context i {
  font-size: 0.7rem;
  opacity: 0.5;
  margin-right: 4px;
}

.term-score {
  margin-top: var(--spacing-xs);
}

.score-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 4px;
}

.score-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #3b82f6);
  transition: width 0.3s ease;
}

.score-label {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.5);
}

.keywords-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  flex-wrap: wrap;
  gap: var(--spacing-sm);
}

.extraction-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(99, 102, 241, 0.2));
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 20px;
  font-size: 0.8rem;
  color: #c4b5fd;
  position: relative;
  cursor: help;
}

.extraction-badge i {
  color: #a78bfa;
}

.badge-tooltip {
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 6px;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.extraction-badge:hover .badge-tooltip {
  display: block;
}

/* Enhanced importance styling */
.term-card.importance-high {
  border-width: 2px;
  box-shadow: 0 0 0 1px rgba(251, 191, 36, 0.2);
}

.term-card.source-llm {
  background: rgba(139, 92, 246, 0.05);
}

.term-card.source-chapter {
  background: rgba(16, 185, 129, 0.05);
}

.term-actions {
  display: flex;
  gap: var(--spacing-xs);
  flex-wrap: wrap;
}

.term-action-btn {
  flex: 1;
  min-width: 120px;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.term-action-btn.search-btn {
  background: #10b981;
  color: white;
}

.term-action-btn.search-btn:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.term-action-btn.jump-btn {
  background: rgba(59, 130, 246, 0.8);
  color: white;
}

.term-action-btn.jump-btn:hover {
  background: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.term-timestamps {
  margin-top: var(--spacing-xs);
  padding-top: var(--spacing-xs);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.timestamps-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: var(--spacing-xs);
  display: flex;
  align-items: center;
  gap: 6px;
}

.timestamp-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.timestamp-item {
  padding: 8px 12px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.timestamp-item:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: rgba(59, 130, 246, 0.5);
  transform: translateX(4px);
}

.timestamp-time {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #93c5fd;
  font-size: 0.85rem;
  font-weight: 600;
  font-family: monospace;
}

.timestamp-time i {
  font-size: 0.75rem;
  color: #60a5fa;
}

.timestamp-context {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  font-style: italic;
  line-height: 1.4;
  padding-left: 20px;
  border-left: 2px solid rgba(59, 130, 246, 0.3);
}

.more-timestamps {
  padding: 8px 12px;
  text-align: center;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

/* Manual Search Results */
.manual-search-results {
  margin: var(--spacing-xl) 0;
  padding: var(--spacing-lg);
  background: rgba(16, 185, 129, 0.1);
  border-radius: 8px;
  border-left: 4px solid #10b981;
}

.manual-search-results h4 {
  color: white;
  margin: 0 0 var(--spacing-xs) 0;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.search-results-info {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  margin-bottom: var(--spacing-lg);
}

/* Auto Research Results */
.auto-research-results {
  margin: var(--spacing-xl) 0;
}

.auto-research-results h4 {
  color: white;
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 1.2rem;
}

.auto-research-info {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: var(--spacing-lg);
}

.research-query-group {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.query-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #10b981;
}

.articles-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

/* Analyze Prompt */
.analyze-prompt {
  text-align: center;
  padding: var(--spacing-xxl);
}

.analyze-prompt .btn-lg {
  font-size: 1.1rem;
  padding: var(--spacing-md) var(--spacing-xl);
  margin-bottom: var(--spacing-md);
}

.analyze-info {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-top: var(--spacing-md);
}

/* Manual Research Section */
.research-section {
  max-width: 1200px;
  margin: var(--spacing-xxl) auto;
  padding: var(--spacing-xl);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.research-header {
  margin-bottom: var(--spacing-xl);
}

.research-header h3 {
  font-size: 1.5rem;
  margin: 0 0 var(--spacing-sm) 0;
  color: white;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.research-subtitle {
  color: #aaa;
  margin: 0;
}

.research-search {
  margin-bottom: var(--spacing-xl);
}

.search-input-group {
  display: flex;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

.search-input {
  flex: 1;
  padding: var(--spacing-md);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 1rem;
}

.search-input::placeholder {
  color: #888;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary);
  background: rgba(255, 255, 255, 0.15);
}

.search-btn {
  flex-shrink: 0;
  padding: var(--spacing-md) var(--spacing-xl);
}

.search-filters {
  display: flex;
  gap: var(--spacing-lg);
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  color: #ccc;
  cursor: pointer;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: 4px;
  transition: all 0.2s ease;
}

.filter-checkbox:hover {
  background: rgba(255, 255, 255, 0.05);
}

.filter-checkbox input[type="checkbox"] {
  cursor: pointer;
}

.no-connections {
  padding: var(--spacing-md);
  background: rgba(255, 193, 7, 0.1);
  border-radius: 4px;
  color: #856404;
}

.research-loading {
  text-align: center;
  padding: var(--spacing-xxl);
  color: #aaa;
}

.research-results {
  margin-top: var(--spacing-xl);
}

.results-header h4 {
  margin: 0 0 var(--spacing-lg) 0;
  color: white;
  font-size: 1.1rem;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.result-item {
  padding: var(--spacing-lg);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border-left: 3px solid var(--color-primary);
  transition: all 0.2s ease;
}

.result-item:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateX(4px);
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-md);
  gap: var(--spacing-md);
}

.result-header h5 {
  margin: 0;
  color: white;
  font-size: 1.1rem;
  line-height: 1.4;
  flex: 1;
}

.result-source {
  padding: 4px 12px;
  background: rgba(var(--color-primary-rgb), 0.2);
  color: var(--color-primary);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
}

.result-abstract {
  color: #ccc;
  line-height: 1.6;
  margin: 0 0 var(--spacing-md) 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.result-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  font-size: 0.9rem;
  color: #999;
}

.result-meta span {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.result-meta i {
  color: var(--color-primary);
}

.result-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.btn-small {
  padding: var(--spacing-xs) var(--spacing-md);
  font-size: 0.9rem;
}

.research-empty,
.research-initial {
  text-align: center;
  padding: var(--spacing-xxl);
  color: #aaa;
}

.research-empty i,
.research-initial i {
  font-size: 3rem;
  margin-bottom: var(--spacing-md);
  opacity: 0.5;
}

.quick-searches {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-lg);
  align-items: center;
}

.quick-search-btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.quick-search-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: var(--color-primary);
}

/* Quiz Prompt Section */
.quiz-prompt-section {
  margin: var(--spacing-xl) 0;
  animation: slideInUp 0.5s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.quiz-prompt-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--spacing-xxl);
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%);
  border: 2px solid rgba(16, 185, 129, 0.4);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(16, 185, 129, 0.2);
}

.quiz-prompt-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  margin-bottom: var(--spacing-lg);
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.4);
}

.quiz-prompt-icon i {
  font-size: 2.5rem;
  color: white;
}

.quiz-prompt-content h3 {
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
  margin: 0 0 var(--spacing-sm) 0;
}

.quiz-prompt-content p {
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 var(--spacing-lg) 0;
  max-width: 500px;
}

.quiz-prompt-stats {
  display: flex;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  flex-wrap: wrap;
  justify-content: center;
}

.quiz-prompt-stats span {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  padding: var(--spacing-xs) var(--spacing-md);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
}

.quiz-prompt-stats i {
  color: #10b981;
}

.quiz-prompt-btn {
  padding: var(--spacing-md) var(--spacing-xxl) !important;
  font-size: 1.2rem !important;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.4);
  transition: all 0.3s ease !important;
}

.quiz-prompt-btn:hover {
  transform: translateY(-3px) scale(1.02) !important;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.5) !important;
}

/* Responsive */
@media (max-width: 1200px) {
  .video-section {
    grid-template-columns: 1fr;
  }

  .course-sidebar {
    max-height: none;
    border-left: none;
    border-top: 1px solid #333;
  }
}

@media (max-width: 768px) {
  .video-container {
    padding: var(--spacing-sm);
  }

  .video-title-top {
    font-size: 1.3rem;
  }

  .video-title {
    font-size: 1.3rem;
  }

  .timestamps-section,
  .resources-section,
  .research-section {
    padding: var(--spacing-md);
  }

  .search-input-group {
    flex-direction: column;
  }

  .search-btn {
    width: 100%;
  }

  .result-header {
    flex-direction: column;
  }

  .result-source {
    align-self: flex-start;
  }
}
</style>

